import type { BonusState } from '../bonus/types';
import type { MoneyCents } from '../core/types';
import type { ReplayPresentationMode, ReplayDraftV2 } from '../replay/schema';
import type { PersistedSessionV2 } from '../services/persistence';
import { EXTREME_PAID_SPINS, type ExtremeRunView } from '../extreme/types';

export type GamePhase =
  | 'idle'
  | 'spinning'
  | 'recovering'
  | 'feature-intro'
  | 'feature-ready'
  | 'feature-summary'
  | 'extreme-summary';

export interface FeatureSummaryView {
  readonly totalWinCents: MoneyCents;
  readonly spinsPlayed: number;
  readonly totalAwardedSpins: number;
  readonly stickyWildCount: number;
  readonly retriggerCount: number;
  readonly potOfGreedTriggered: boolean;
}

export interface CompletedFeature extends FeatureSummaryView {
  readonly replayDraft: ReplayDraftV2 | null;
}

export interface RankedRunView {
  readonly id: string;
  readonly username: string;
  readonly betCents: MoneyCents;
  readonly startBalanceCents: MoneyCents;
  readonly startedAt: number;
  readonly currentX: number;
}

export interface GameViewModel {
  readonly phase: GamePhase;
  readonly balanceCents: MoneyCents;
  readonly betCents: MoneyCents;
  readonly lastWinCents: MoneyCents;
  readonly turbo: boolean;
  readonly presentationMode: ReplayPresentationMode;
  readonly autoEnabled: boolean;
  readonly autoStopEnabled: boolean;
  readonly autoStopThresholdX: number;
  readonly bonus: BonusState | null;
  readonly featureSummary: FeatureSummaryView | null;
  readonly rankedRun: RankedRunView | null;
  readonly extremeRun: ExtremeRunView | null;
  readonly canSpin: boolean;
  readonly canChangeBet: boolean;
  readonly canStartFeature: boolean;
  readonly canCloseSummary: boolean;
  readonly canToggleAuto: boolean;
}

export type GameSessionState = PersistedSessionV2;

export function deriveStablePhase(session: GameSessionState): Exclude<GamePhase, 'spinning' | 'recovering'> {
  const bonus = session.bonus;
  if (bonus === null) {
    if (session.extremeRun?.paidSpinsCompleted === EXTREME_PAID_SPINS) return 'extreme-summary';
    return 'idle';
  }
  if (!bonus.started) return 'feature-intro';
  if (bonus.active) return 'feature-ready';
  return 'feature-summary';
}

export function createFeatureSummary(bonus: BonusState | null): FeatureSummaryView | null {
  if (bonus === null || bonus.active || !bonus.started) return null;
  return Object.freeze({
    totalWinCents: bonus.totalWinCents,
    spinsPlayed: bonus.spinsPlayed,
    totalAwardedSpins: bonus.totalAwardedSpins,
    stickyWildCount: bonus.stickyWilds.length,
    retriggerCount: bonus.retriggerCount,
    potOfGreedTriggered: bonus.pogTriggered,
  });
}
